import { type Dispatch, type SetStateAction, useEffect, useState } from 'react';

/**
 * A hook that syncs state with localStorage.
 *
 * @param key The localStorage key to use.
 * @param initialState The initial value to use if no value is found in localStorage.
 * @returns A stateful value, and a function to update it.
 */
function usePersistentState<T>(
    key: string,
    initialState: T,
): [T, Dispatch<SetStateAction<T>>] {
    const [state, setState] = useState<T>(() => {
        if (typeof window === 'undefined') {
            return initialState;
        }
        try {
            const storedValue = window.localStorage.getItem(key);
            return storedValue ? JSON.parse(storedValue) : initialState;
        } catch (error) {
            console.warn(`Error reading localStorage key "${key}":`, error);
            return initialState;
        }
    });

    useEffect(() => {
        if (typeof window !== 'undefined') {
            try {
                window.localStorage.setItem(key, JSON.stringify(state));
            } catch (error) {
                console.warn(`Error setting localStorage key "${key}":`, error);
            }
        }
    }, [key, state]);

    return [state, setState];
}

export default usePersistentState;
